import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from "react-router-dom";
import { localizeArt } from './../localize.js';
import MyUserContext from './../myUserContext.js';
import PlatformRatings from './PlatformRatings';
import ArtThumbnail from './ArtThumbnail';
import { Button, Text, Badge } from '@mantine/core';

// Card is w-40 (160px) plus a gap-4 (16px) between siblings. Enough
// precision to compute "how many cards fit in one row" for the fit
// logic below.
const CARD_WIDTH_PX = 160;
const GAP_PX = 16;

const CATEGORY_ENUM = {
  1: "movie",
  2: "videoGame",
  3: "book"
};

// Stable module-level default. Using an inline `= ["movie", ...]` in
// the destructured props gives a fresh array reference on every
// render, which makes the fit-measurement effects loop forever
// (setState → re-render → new deps → effect → setState).
const DEFAULT_CATEGORIES = ["movie", "book", "videoGame"];

const ImageScroller = ({
  arts,
  categories = DEFAULT_CATEGORIES,
  showCategoryTitles = true,
  aiReasonedArts = [],
  basedOnRecentLogbookArts = []
}) => {
  const myUser = useContext(MyUserContext);
  const language = myUser ? myUser.myUserSettings.lang : 'ru';
  const scrollerRefs = useRef({});
  // Per-category fit info: { singleRow: bool, needsScroll: bool }.
  // Measured from the actual scroll container so the "one row + no
  // scroll buttons" collapse works at every viewport width.
  const [fitByCat, setFitByCat] = useState({});

  const scrollByAmount = (cat, direction) => {
    const scroller = scrollerRefs.current[cat];
    if (!scroller) return;
    const amount = Math.round(scroller.clientWidth * 0.8);
    scroller.scrollBy({ left: direction * amount, behavior: "smooth" });
  };

  const measureFit = () => {
    const next = {};
    categories.forEach((cat) => {
      const scroller = scrollerRefs.current[cat];
      if (!scroller) return;
      const count = arts.filter((item) => CATEGORY_ENUM[item.category] === cat).length;
      if (count === 0) return;
      const visibleCols = Math.max(1, Math.floor((scroller.clientWidth + GAP_PX) / (CARD_WIDTH_PX + GAP_PX)));
      // Single row when everything fits in one line — no second row
      // sitting half-empty. needsScroll independently controls the
      // left/right buttons.
      const singleRow = count <= visibleCols;
      const needsScroll = singleRow ? count > visibleCols : count > visibleCols * 2;
      next[cat] = { singleRow, needsScroll };
    });
    setFitByCat(next);
  };

  useLayoutEffect(() => {
    measureFit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arts, categories]);

  useEffect(() => {
    const onResize = () => measureFit();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arts, categories]);

  return (
    <div className="space-y-8">
      {categories.map((cat) => {
        const filteredArts = arts.filter(
          (item) => CATEGORY_ENUM[item.category] === cat
        );

        if (filteredArts.length === 0) return null;

        return (
          <div key={cat} className="space-y-3">
            {/* Category title */}
            {showCategoryTitles && (
              <div className="flex justify-center font-semibold capitalize mt-3" style={{ color: 'var(--mantine-color-dimmed)' }}>
                {cat === "movie"
                  ? (language === "ru" ? "Фильмы" : "Movies")
                  : cat === "book"
                  ? (language === "ru" ? "Книги" : "Books")
                  : (language === "ru" ? "Игры" : "Video Games")}
              </div>
            )}

            {/* Two-row carousel (collapses to one row + hides scroll
                buttons when the whole set fits in the viewport). */}
            <div className="relative">
              {fitByCat[cat]?.needsScroll && (
                <Button
                  type="button"
                  aria-label="Scroll left"
                  variant="filled"
                  color="dark"
                  size="sm"
                  className="hidden md:flex absolute left-1 top-1/2 -translate-y-1/2 z-10 h-12 w-12 items-center justify-center rounded-full shadow-md"
                  onClick={() => scrollByAmount(cat, -1)}
                >
                  ‹
                </Button>
              )}
              <div
                ref={(el) => { scrollerRefs.current[cat] = el; }}
                className={`grid ${fitByCat[cat]?.singleRow ? 'grid-rows-1' : 'grid-rows-2'} grid-flow-col auto-cols-max gap-4 overflow-x-auto pb-2 pr-6 snap-x snap-mandatory`}
                style={{ scrollbarWidth: "none" }}
              >
                {filteredArts.map((item, index) => {
                  const localizedArt = localizeArt(item, language);
                  const rawDescription = (localizedArt.description || "").trim();
                  const sentences = rawDescription.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [];
                  const firstSentences = sentences.slice(0, 3).join(" ").trim();
                  let description = firstSentences || rawDescription;
                  if (description.length > 150) {
                    description = `${description.slice(0, 147).trim()}...`;
                  }
                  return (
                  <div key={index + item.id} className="snap-start">
                    <Link to={`/arts/${item.id}`} className="group block w-40">
                      <div className="rounded-xl overflow-hidden shadow-sm transition-transform duration-200 group-hover:-translate-y-1 group-hover:shadow-lg" style={{ backgroundColor: 'var(--mantine-color-body)' }}>
                        <ArtThumbnail art={item} />
                      </div>
                      <div className="mt-2 text-center px-1">
                        <Text size="xs" fw={500} lineClamp={1} lh="sm">
                          {localizedArt.name}
                        </Text>
                        {description && (
                          <Text size="xs" c="dimmed" lineClamp={2} lh="xs" mt={2}>
                            {description}
                          </Text>
                        )}
                      </div>
                    </Link>
                    <div className="mt-1.5 flex justify-center w-40">
                      <PlatformRatings art={item} center={true} />
                    </div>
                    {aiReasonedArts.includes(item.id) && (
                      <div className="flex justify-center mt-1">
                        <Badge size="xs" color="blue" variant="light">{language === "ru" ? "ИИ" : "AI"}</Badge>
                      </div>
                    )}
                    {basedOnRecentLogbookArts.includes(item.id) && (
                      <div className="flex justify-center mt-1">
                        <Badge size="xs" color="yellow" variant="light">{language === "ru" ? "Из журнала" : "Logbook"}</Badge>
                      </div>
                    )}
                  </div>
                )})}
              </div>
              {fitByCat[cat]?.needsScroll && (
                <Button
                  type="button"
                  aria-label="Scroll right"
                  variant="filled"
                  color="dark"
                  size="sm"
                  className="hidden md:flex absolute right-1 top-1/2 -translate-y-1/2 z-10 h-12 w-12 items-center justify-center rounded-full shadow-md"
                  onClick={() => scrollByAmount(cat, 1)}
                >
                  ›
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ImageScroller;
